import { useRef, useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import { Alert, Box, Button, CircularProgress, Link, TextField, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import MarkEmailReadOutlinedIcon from '@mui/icons-material/MarkEmailReadOutlined';
import { authApi } from '../../authApi';
import { validateEmail } from '../../authValidation';
import { AUTH_ROUTES } from '../../authConstants';

const I18N = 'auth.forgotPassword';

/**
 * Minimum delay (ms) between two reset requests. Coalesces rapid clicks — chiefly
 * on the "Resend" button — so a burst can't spam the backend (each request sends
 * an email). Leading-edge: the first click fires immediately, extras within the
 * window are dropped. The `loading` guard separately blocks concurrent in-flight
 * requests.
 */
const REQUEST_DEBOUNCE_MS = 1000;

// AuthCard forces `text.body` on every Typography (theme text.primary is white,
// for the branded background). Muted copy needs an equal-specificity override.
const MUTED = { '&.MuiTypography-root': { color: 'text.label' } } as const;

/** Back-to-login link (arrow + label), shared by both states. */
function BackToLogin() {
  const { t } = useTranslation('common');
  return (
    <Link
      component={RouterLink}
      to={AUTH_ROUTES.LOGIN}
      underline="hover"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.75,
        fontWeight: 'fontWeightMedium',
      }}
    >
      <ArrowBackIcon fontSize="small" />
      {t(`${I18N}.backToLogin`)}
    </Link>
  );
}

export function ForgotPasswordForm() {
  const { t } = useTranslation('common');
  const theme = useTheme();
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [sentTo, setSentTo] = useState<string | null>(null);
  const lastRequestAt = useRef(0);

  const requestReset = async (value: string) => {
    const now = Date.now();
    // Drop the click if one is already in flight or fired within the debounce window.
    if (loading || now - lastRequestAt.current < REQUEST_DEBOUNCE_MS) return;
    lastRequestAt.current = now;
    setLoading(true);
    try {
      // Best-effort: the reset link is dispatched server-side. Errors are
      // swallowed so the UI never reveals whether the account exists.
      await authApi.requestPasswordReset(value).catch(() => undefined);
      setSentTo(value);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (loading) return;
    const value = email.trim();
    const validationError = validateEmail(value);
    if (validationError) {
      setError(validationError);
      return;
    }
    setError(undefined);
    void requestReset(value);
  };

  // ── Confirmation state ──────────────────────────────────────────────────
  if (sentTo) {
    return (
      <Box
        sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}
      >
        <Box
          sx={theme => ({
            width: theme.avatar.lg,
            height: theme.avatar.lg,
            borderRadius: theme.borderRadius.pill,
            mb: 2.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: alpha(theme.palette.success.main, 0.14),
            color: 'success.main',
          })}
        >
          <MarkEmailReadOutlinedIcon />
        </Box>

        <Typography variant="h5" component="h1" sx={{ fontWeight: 'fontWeightMedium', mb: 1 }}>
          {t(`${I18N}.sentTitle`)}
        </Typography>
        <Typography variant="body2" sx={MUTED}>
          <Trans
            i18nKey={`${I18N}.sentMessage`}
            t={t}
            values={{ email: sentTo }}
            components={{
              strong: (
                <Box component="span" sx={{ fontWeight: 'fontWeightMedium', color: 'text.body' }} />
              ),
            }}
          />
        </Typography>

        <Alert severity="success" sx={{ width: '100%', mt: 2.5, textAlign: 'left' }}>
          {t(`${I18N}.sentAlert`)}
        </Alert>

        <Button
          fullWidth
          variant="outlined"
          size="large"
          onClick={() => void requestReset(sentTo)}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={theme.iconSize.sm} color="inherit" /> : null}
          sx={{ mt: 3, mb: 2 }}
        >
          {t(`${I18N}.resend`)}
        </Button>

        <BackToLogin />
      </Box>
    );
  }

  // ── Request state ───────────────────────────────────────────────────────
  return (
    <Box component="form" onSubmit={handleSubmit} noValidate sx={{ width: '100%' }}>
      <Typography variant="h5" component="h1" sx={{ fontWeight: 'fontWeightMedium', mb: 1 }}>
        {t(`${I18N}.title`)}
      </Typography>
      <Typography variant="body2" sx={MUTED}>
        {t(`${I18N}.subtitle`)}
      </Typography>

      <TextField
        label={t(`${I18N}.fields.email`)}
        name="email"
        type="email"
        autoComplete="email"
        placeholder={t(`${I18N}.fields.emailPlaceholder`)}
        autoFocus
        fullWidth
        required
        margin="normal"
        value={email}
        onChange={e => {
          setEmail(e.target.value);
          setError(undefined);
        }}
        error={Boolean(error)}
        helperText={error ? t(error) : undefined}
        disabled={loading}
      />

      <Button
        type="submit"
        fullWidth
        variant="contained"
        size="large"
        disabled={loading}
        startIcon={loading ? <CircularProgress size={theme.iconSize.sm} color="inherit" /> : null}
        sx={{ mt: 3, mb: 2 }}
      >
        {loading ? t(`${I18N}.submitting`) : t(`${I18N}.submit`)}
      </Button>

      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <BackToLogin />
      </Box>
    </Box>
  );
}
